import { Component, Inject, Input, Output, EventEmitter, OnInit } from 'ng-metadata/core';

interface FallbackColor {
    bgColor: string;
    textColor: string;
}

/**
 * @ngdoc directive
 * @name mwSourceAvatar
 * @module mw.components.sourceAvatar
 *
 * @param {string} [link-url] - The url of the document
 * @param {string} [image-url] - The URL of the avatar
 * @param {string} [source-name] - Name of the source, required for avatar generation if favicon doesn't exist
 * @param {string} [document-title] - Alt title of the avatar image.
 * @param {boolean} [is-rounded] - Flag to tell if the avatar is rounded or not.
 * @param {number} [initials-size=1] - Determine the number of initials in the avatar, can be 1 or 2, in case of 2 characters, the source-name must have the firstname and lastname
 * @param {string} [media-type-icon] - Name of the media type icon to be displayed (eg. 'facebook', 'twitter', 'youtube', 'instagram', etc..)
 * @param {expression} [on-click] - Angular expression to evaluate when the avatar is clicked.  $event is the ng-click event.
 *
 * @description
 * `<mw-source-avatar`> is a component that renders the avatar for a content-document component
 *
 * @usage
 * <hljs hljs-language="html">
 *  <mw-source-avatar title="some-title" link-url="https://www.google.com" image-url="http://some-url.com/cool.png"> </mw-source-avatar>
 * </hljs>
 */
@Component({
    selector: 'mw-source-avatar',
    template: `
        <a ng-href="{{::$ctrl.linkUrl}}"
            target="_blank"
            class="mw-source-avatar" target="_blank"
            data-tracking-event="documentOperation/open"
            ng-click="$ctrl.handleAvatarClick($event);"
            ng-class="{'mw-source-avatar--rounded': $ctrl.isRounded, 'mw-source-avatar--loaded': !$ctrl.isLoading, 'mw-source-avatar--has-favicon': $ctrl.faviconLoaded, 'mw-source-avatar--no-favicon': !$ctrl.faviconLoaded}">
            <img class="mw-source-avatar__image"
                ng-src="{{::$ctrl.imageUrl}}"
                alt="{{::$ctrl.documentTitle | mwStripTags}}"
                title="{{::$ctrl.sourceName | mwStripTags}}"></img>
            <div class="mw-source-avatar__placeholder"
                ng-style="::{'background-color':$ctrl.fallbackColor.bgColor,'color':$ctrl.fallbackColor.textColor}"
                title="{{::$ctrl.sourceName | mwStripTags}}">
                <span ng-class="::{'mw-source-avatar__2-initials': $ctrl.initialsSize === 2 }">{{::$ctrl.sourceFirstLetters}}</span>
            </div>
            <div ng-if="::$ctrl.mediaTypeIcon && $ctrl.mediaTypeIcon !== 'news'" class="mw-source-avatar__media-type-icon">
                <mw-icon class="mw-content-document-media-type-icon" svg-src="{{$ctrl.mediaTypeIconUrl}}" size="xs"></mw-icon>
            </div>

        </a>
        `
})
export class SourceAvatarComponent implements OnInit {
    @Input('<') sourceName: string;
    @Input('<') imageUrl: string;
    @Input('<') linkUrl: string;
    @Input('<') documentTitle: string;
    @Input('<') isRounded: boolean = false;
    @Input('<') initialsSize: number = 1;
    @Input('<') mediaTypeIcon: string;
    @Input('<') fallbackColors: FallbackColor[] = [
        { bgColor: '#0194EB', textColor: 'rgba(255,255,255,0.7)'}, // Water
        { bgColor: '#FFBC45', textColor: 'rgba(0,0,0,0.54)'}, // Mango
        { bgColor: '#F65D8B', textColor: 'rgba(255,255,255,0.7)'}, // Flamingo
        { bgColor: '#1DD1A1', textColor: 'rgba(0,0,0,0.54)'}, // Emerald
        { bgColor: '#F67747', textColor: 'rgba(0,0,0,0.54)'}, // Pumpkin
        { bgColor: '#545454', textColor: 'rgba(255,255,255,0.7)'}, // Charcoal
        { bgColor: '#96497B', textColor: 'rgba(255,255,255,0.7)'}, // Lavender
        { bgColor: '#3E9189', textColor: 'rgba(255,255,255,0.7)'}, // Pine
        { bgColor: '#956445', textColor: 'rgba(255,255,255,0.7)'}, // Coco
        { bgColor: '#3B5C87', textColor: 'rgba(255,255,255,0.7)'}, // Deep Space
    ];

    @Output() onClick = new EventEmitter<ng.IAngularEvent>();

    sourceFirstLetters: string;
    faviconLoaded: boolean = false;
    isLoading: boolean = true;
    mediaTypeIconUrl: string;
    fallbackColor: FallbackColor;

    constructor(
        @Inject('$scope') private $scope: ng.IScope,
        @Inject('$window') private $window: ng.IWindowService,
        @Inject('$translate') private $translate,
        @Inject('mwEnvDetector') private mwEnvDetector
    ) {}

    ngOnInit() {
        if (this.sourceName) {
            if (this.sourceName === 'Unknown') {
                this.sourceFirstLetters = '?';
            } else {
                let firstLetterIndex = 0;

                if (this.sourceName.charAt(0) === '@') {
                    firstLetterIndex = 1;
                }

                this.sourceFirstLetters = this.sourceName.charAt(firstLetterIndex).toUpperCase();

                if (this.initialsSize === 2 && this.sourceName.split(' ').length > 1) {
                    this.sourceFirstLetters += this.sourceName.split(' ')[1].charAt(0).toUpperCase();
                }
            }
        }
        this.fallbackColor = getFallbackColor(this.sourceName, this.fallbackColors);

        this.loadImage(this.imageUrl);

        if (this.mediaTypeIcon) {
            let mediaTypeSvgName = this.mediaTypeIcon + '_color';
            if (this.mediaTypeIcon === 'instagram' && (this.mwEnvDetector.browser === 'ie' || this.mwEnvDetector.browser === 'ms-edge' || this.mwEnvDetector.browser === 'safari')) {
                mediaTypeSvgName += '_simple';
            }

            this.mediaTypeIconUrl = `https://assets.meltwater.io/flux-icons/latest/mediatypes/svg/${mediaTypeSvgName}.svg`;
        }

    }

    loadImage(url: string) {

        if (url) {
            let img = this.$window.document.createElement('img');

            img.onload = () => {
                if (img.naturalHeight >= 48 && img.naturalWidth >= 48) {
                    this.faviconLoaded = true;
                }

                this.isLoading = false;
                this.$scope.$evalAsync();
                img.remove();
            };
            img.onerror = () => {
                this.isLoading = false;
                this.$scope.$evalAsync();
                // the image has failed
            };
            img.src = url;
        } else {
            this.isLoading = false;
        }
    }

    handleAvatarClick(event: ng.IAngularEvent) {
        this.onClick.emit(event);
    }
}

function getFallbackColor(str, colors) {
    const hashedStr = hash(str);
    const colorIndex = hashedStr % colors.length;

    return colors[colorIndex];
}

/* hash function from https://github.com/darkskyapp/string-hash */
function hash(str) {
    let hash = 5381;
    let i = str.length;

    while (i) {
        hash = (hash * 33) ^ str.charCodeAt(--i);
    }

    /* JavaScript does bitwise operations (like XOR, above) on 32-bit signed
        * integers. Since we want the results to be always positive, convert the
        * signed int to an unsigned by doing an unsigned bitshift. */
    return hash >>> 0;
}



// WEBPACK FOOTER //
// ./src/lib/components/source-avatar/source-avatar.component.ts